import React, { useState } from 'react';
import { SearchIcon, ChevronRightIcon, SirenIcon, DollarSignIcon, PhoneCallIcon, ClipboardListIcon } from 'lucide-react';
interface Service {
  id: string;
  title: string;
  description: string;
  category: string;
  estimatedTime: string;
  fee: string;
}
const services: Service[] = [
// Emergency Fees & Levies
{
  id: 'ef1',
  title: 'EMS Availability Fee Payment',
  description: 'Process annual emergency medical services availability fee',
  category: 'Emergency Fees & Levies',
  estimatedTime: 'Same day',
  fee: 'Varies by property'
}, {
  id: 'ef2',
  title: 'Emergency Dispatch (911) Levy',
  description: 'Pay annual emergency dispatch system maintenance levy',
  category: 'Emergency Fees & Levies',
  estimatedTime: 'Same day',
  fee: 'Based on assessment'
}, {
  id: 'ef3',
  title: 'Emergency Services Fee Exemption',
  description: 'Apply for exemption from emergency services fees',
  category: 'Emergency Fees & Levies',
  estimatedTime: '5-7 business days',
  fee: 'No fee'
},
// Emergency Response
{
  id: 'er1',
  title: 'Emergency Response Registration',
  description: 'Register property details for emergency response planning',
  category: 'Emergency Response',
  estimatedTime: '2-3 business days',
  fee: 'No fee'
}, {
  id: 'er2',
  title: 'Medical Alert Registration',
  description: 'Register medical conditions for emergency responders',
  category: 'Emergency Response',
  estimatedTime: '1-2 business days',
  fee: 'No fee'
}, {
  id: 'er3',
  title: 'Special Needs Registry',
  description: 'Register special needs for emergency evacuation assistance',
  category: 'Emergency Response',
  estimatedTime: '2-3 business days',
  fee: 'No fee'
},
// Emergency Planning
{
  id: 'ep1',
  title: 'Emergency Plan Development',
  description: 'Create or update property emergency response plan',
  category: 'Emergency Planning',
  estimatedTime: '5-7 business days',
  fee: '$150.00'
}, {
  id: 'ep2',
  title: 'Emergency Contact Update',
  description: 'Update emergency contact information for properties',
  category: 'Emergency Planning',
  estimatedTime: '1-2 business days',
  fee: 'No fee'
}, {
  id: 'ep3',
  title: 'Emergency Training Registration',
  description: 'Register for community emergency response training',
  category: 'Emergency Planning',
  estimatedTime: '3-5 business days',
  fee: '$75.00 per person'
}];
const CategoryIcon = ({
  category
}: {
  category: string;
}) => {
  switch (category) {
    case 'Emergency Fees & Levies':
      return <DollarSignIcon className="h-6 w-6 text-green-600" data-id="element-3683" />;
    case 'Emergency Response':
      return <PhoneCallIcon className="h-6 w-6 text-red-600" data-id="element-3684" />;
    case 'Emergency Planning':
      return <ClipboardListIcon className="h-6 w-6 text-blue-600" data-id="element-3685" />;
    default:
      return <SirenIcon className="h-6 w-6 text-gray-600" data-id="element-3686" />;
  }
};
const ServiceCard = ({
  service
}: {
  service: Service;
}) => {
  return <div className="bg-white rounded-lg border border-gray-200 hover:border-blue-500 hover:shadow-sm transition-all duration-200" data-id="element-3687">
      <div className="p-5 flex flex-col h-full" data-id="element-3688">
        <div className="flex-1" data-id="element-3689">
          <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-3690">
            {service.title}
          </h3>
          <p className="text-sm text-gray-600 mb-6 line-clamp-2" data-id="element-3691">
            {service.description}
          </p>
        </div>
        <div className="space-y-4" data-id="element-3692">
          <div className="flex items-center space-x-4 text-sm text-gray-500 pt-4 border-t border-gray-100" data-id="element-3693">
            <div className="flex items-center" data-id="element-3694">
              <span className="inline-block w-4 h-4 text-gray-400 mr-1.5" data-id="element-3695">
                ⏱
              </span>
              <span data-id="element-3696">{service.estimatedTime}</span>
            </div>
            <div className="flex items-center" data-id="element-3697">
              <span className="inline-block w-4 h-4 text-gray-400 mr-1.5" data-id="element-3698">
                💰
              </span>
              <span data-id="element-3699">{service.fee}</span>
            </div>
          </div>
          <button className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors duration-150" data-id="element-3700">
            Process Payment
            <span className="ml-1" data-id="element-3701">→</span>
          </button>
        </div>
      </div>
    </div>;
};
const CategorySection = ({
  category,
  services
}: {
  category: string;
  services: Service[];
}) => {
  return <div className="mb-8" data-id="element-3702">
      <div className="flex items-center mb-4" data-id="element-3703">
        <CategoryIcon category={category} data-id="element-3704" />
        <h2 className="text-xl font-semibold text-gray-900 ml-2" data-id="element-3705">{category}</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3706">
        {services.map(service => <ServiceCard key={service.id} service={service} data-id="element-3707" />)}
      </div>
    </div>;
};
export const EmergencyServicesPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const filteredServices = services.filter(service => service.title.toLowerCase().includes(searchQuery.toLowerCase()) || service.description.toLowerCase().includes(searchQuery.toLowerCase()));
  const categories = Array.from(new Set(filteredServices.map(service => service.category)));
  return <div className="min-h-screen bg-gray-50" data-id="element-3708">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-3709">
        {/* Breadcrumb */}
        <div className="flex items-center text-sm text-gray-500 mb-8" data-id="element-3710">
          <a href="/" className="hover:text-gray-700" data-id="element-3711">
            Dashboard
          </a>
          <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-3712" />
          <span className="text-gray-900" data-id="element-3713">Emergency Services</span>
        </div>
        {/* Header */}
        <div className="mb-8" data-id="element-3714">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-3715">
            Emergency Services
          </h1>
          <p className="text-gray-600" data-id="element-3716">
            Manage emergency service fees, response planning, and related
            services
          </p>
        </div>
        {/* Search */}
        <div className="mb-8" data-id="element-3717">
          <div className="max-w-md relative" data-id="element-3718">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3719">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-3720" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Search services..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-3721" />
          </div>
        </div>
        {/* Service Categories */}
        {categories.map(category => <CategorySection key={category} category={category} services={filteredServices.filter(service => service.category === category)} data-id="element-3722" />)}
      </div>
    </div>;
};